import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Button
} from '@mui/material';
import {
  AccountCircle,
  ExitToApp,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  FavoriteBorder as HealthIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import Login from './components/Login.tsx';
import OAuthCallback from './components/OAuthCallback.tsx';
import PipelineForm from './components/PipelineForm';
import { PipelineFlowWithRef, PipelineFlowHandle } from './components/PipelineFlow.tsx';
import ModelConfiguration from './components/ModelConfiguration.tsx';
import HealthStatus from './components/HealthStatus.tsx';
import PipelineMonitor from './components/PipelineMonitor.tsx';
import { startPipelineStream, healthCheck as dataLakeHealthCheck, UpdateType, StepNames } from './services/datalakeGrpcService.ts';
import { healthCheck as seedHealthCheck } from './services/seedGrpcService.ts';
import { authService, UserProfile } from './services/authService.ts';

// Create Material-UI theme - matching PrecisionAI logo colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#1ABC9C',
      light: '#48D5B5',
      dark: '#16A085',
    },
    secondary: {
      main: '#00D4AA',
      light: '#33DDB8',
      dark: '#00B890',
    },
    background: {
      default: '#F8FAFB',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C3E50',
      secondary: '#7F8C8D',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.08)',
    '0px 8px 16px rgba(0, 0, 0, 0.1)',
    '0px 12px 24px rgba(0, 0, 0, 0.12)',
    '0px 16px 32px rgba(0, 0, 0, 0.14)',
    '0px 20px 40px rgba(0, 0, 0, 0.16)',
    ...Array(18).fill('0px 24px 48px rgba(0, 0, 0, 0.18)'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

// Inner component that can use useLocation
function AppContent() {
  const location = useLocation();

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Pipeline state - updated for streaming
  const [pipelineState, setPipelineState] = useState({
    isRunning: false,
    progress: 0,
    currentStep: 0,
    currentStepName: '',
    steps: {} as { [key: number]: { name: string; status: string; message: string; data: any } },
    error: null as string | null,
    completed: false,
    batchId: '',
    imagesDownloaded: 0,
    weedInstancesExtracted: 0
  });
  const [currentStream, setCurrentStream] = useState<any>(null);

  // Ref for PipelineFlow component
  const pipelineFlowRef = React.useRef<PipelineFlowHandle>(null);

  // System health state
  const [systemHealthy, setSystemHealthy] = useState<boolean | null>(null);
  const [healthCheckLoading, setHealthCheckLoading] = useState(false);

  // Check for existing OAuth auth on mount and when location changes
  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('[AppGrpcWeb] Checking authentication status...');
        const isAuth = await authService.isAuthenticated();

        if (isAuth) {
          const userProfile = await authService.getUserProfile();
          if (userProfile) {
            console.log('[AppGrpcWeb] User authenticated:', userProfile.username);
            setIsAuthenticated(true);
            setUser(userProfile);
          } else {
            console.log('[AppGrpcWeb] No valid user profile');
            setIsAuthenticated(false);
            setUser(null);
          }
        } else {
          console.log('[AppGrpcWeb] User not authenticated');
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('[AppGrpcWeb] Auth check error:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, [location.pathname]); // Re-check when route changes

  // Start token monitoring when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    console.log('[AppGrpcWeb] Starting token monitoring...');
    const stopMonitoring = authService.startTokenMonitoring(30); // Check every 30 seconds

    return () => {
      stopMonitoring(); // Cleanup when unmounting or user logs out
    };
  }, [isAuthenticated]);

  // Check system health periodically
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSystemHealth = async () => {
      setHealthCheckLoading(true);
      let dataLakeHealthy = false;
      let seedHealthy = false;

      // Check DataLake server
      try {
        const dataLakeResponse = await dataLakeHealthCheck();
        dataLakeHealthy = dataLakeResponse.healthy || false;
      } catch (err) {
        console.error('[AppGrpcWeb] DataLake health check failed:', err);
        dataLakeHealthy = false;
      }

      // Check Seed server
      try {
        const seedResponse = await seedHealthCheck();
        seedHealthy = seedResponse.status === 1; // 1 = HEALTHY
      } catch (err) {
        console.error('[AppGrpcWeb] Seed health check failed:', err);
        seedHealthy = false;
      }

      // System is healthy only if both servers are healthy
      setSystemHealthy(dataLakeHealthy && seedHealthy);
      setHealthCheckLoading(false);
    };

    // Check immediately
    checkSystemHealth();

    // Check every 30 seconds
    const interval = setInterval(checkSystemHealth, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handlePipelineStart = (pipelineParams: any) => {
    console.log('[AppGrpcWeb] Starting pipeline with params:', pipelineParams);

    // Check if a pipeline is already running
    if (pipelineState.isRunning && currentStream) {
      const confirmStart = window.confirm(
        'A pipeline is already running. Starting a new pipeline will cancel the current one. Continue?'
      );
      if (!confirmStart) {
        return; // User cancelled
      }
      // Cancel the current stream
      console.log('[AppGrpcWeb] Cancelling previous pipeline stream...');
      currentStream.cancel();
    }

    // Reset pipeline state
    setPipelineState({
      isRunning: true,
      progress: 0,
      currentStep: 0,
      currentStepName: '',
      steps: {},
      error: null,
      completed: false,
      batchId: '',
      imagesDownloaded: 0,
      weedInstancesExtracted: 0
    });

    const stream = startPipelineStream(
      pipelineParams,
      // onUpdate callback - handle streaming updates
      (update) => {
        console.log('[AppGrpcWeb] Pipeline update:', update);

        // Update the PipelineFlow component
        if (pipelineFlowRef.current) {
          pipelineFlowRef.current.updateFromStream(update);
        }

        const updateType = update.updateType;
        const step = update.currentStep;
        const stepName = update.currentStepName || StepNames[step] || `Step ${step}`;
        const progress = update.progressPercentage || 0;

        // Handle different update types
        if (updateType === UpdateType.STEP_STARTED) {
          // Step started
          setPipelineState(prev => ({
            ...prev,
            currentStep: step,
            currentStepName: stepName,
            progress,
            steps: {
              ...prev.steps,
              [step]: {
                name: stepName,
                status: 'running',
                message: 'In progress...',
                data: {}
              }
            }
          }));

        } else if (updateType === UpdateType.STEP_PROGRESS) {
          // Step progress update
          setPipelineState(prev => ({
            ...prev,
            progress,
            steps: {
              ...prev.steps,
              [step]: {
                ...prev.steps[step],
                message: update.selectImagesMessage || update.registerBatchMessage || update.bulkPredictMessage || update.addAnnotationsMessage || 'Processing...',
              }
            }
          }));

        } else if (updateType === UpdateType.STEP_COMPLETED) {
          // Step completed
          const stepData: any = {};

          // Debug logging for step 5 (Bulk Predict)
          if (step === 5) {
            console.log('[DEBUG] Step 5 completion received:', {
              bulkPredictProcessedCount: update.bulkPredictProcessedCount,
              seedOutputUrl: update.seedOutputUrl,
              batchInsightsCount: update.batchInsightsCount,
              fullUpdate: update
            });
          }

          // Extract step-specific data
          if (step === 1 && update.selectImagesS3Uri) {
            stepData['S3 URI'] = update.selectImagesS3Uri;
          } else if (step === 2) {
            stepData['Configuration'] = 'Retrieved';
          } else if (step === 3 && update.batchId) {
            stepData['Batch ID'] = update.batchId;
          } else if (step === 4 && update.bulkPredictProcessedCount) {
            stepData['Images Processed'] = update.bulkPredictProcessedCount;
          } else if (step === 5) {
            // Step 5 is Bulk Predict
            console.log('[DEBUG] Processing step 5 data...');
            if (update.bulkPredictProcessedCount) {
              console.log('[DEBUG] Adding Images Processed:', update.bulkPredictProcessedCount);
              stepData['Images Processed'] = update.bulkPredictProcessedCount;
            }
            // Capture Seed output URL from Step 5 (Bulk Predict)
            if (update.seedOutputUrl) {
              console.log('[DEBUG] ✓ Adding Seed Output URL:', update.seedOutputUrl);
              stepData['Seed Output URL'] = update.seedOutputUrl;
            } else {
              console.warn('[DEBUG] ⚠️  No seedOutputUrl in update!');
            }
            if (update.batchInsightsCount !== undefined) {
              console.log('[DEBUG] Adding Insights Count:', update.batchInsightsCount);
              stepData['Insights Count'] = update.batchInsightsCount;
            }
            console.log('[DEBUG] Final stepData for step 5:', stepData);
          } else if (step === 6) {
            if (update.highWeedImagesCount !== undefined) stepData['High-weed Images'] = update.highWeedImagesCount;
            if (update.weedInstancesExtracted !== undefined) stepData['Weed Instances'] = update.weedInstancesExtracted;
          }

          setPipelineState(prev => ({
            ...prev,
            currentStep: step,
            currentStepName: stepName,
            progress,
            batchId: update.batchId || prev.batchId,
            steps: {
              ...prev.steps,
              [step]: {
                name: stepName,
                status: 'completed',
                message: 'Completed successfully',
                data: stepData
              }
            }
          }));

        } else if (updateType === UpdateType.PIPELINE_COMPLETED) {
          // Pipeline completed
          console.log('[AppGrpcWeb] Pipeline completed successfully!');
          setPipelineState(prev => ({
            ...prev,
            isRunning: false,
            completed: true,
            progress: 100,
            batchId: update.batchId || prev.batchId,
            imagesDownloaded: update.downloadedImagesCount || 0,
            weedInstancesExtracted: update.weedInstancesExtracted || 0
          }));

        } else if (updateType === UpdateType.PIPELINE_ERROR) {
          // Pipeline error
          console.error('[AppGrpcWeb] Pipeline error:', update.errorMessage);
          setPipelineState(prev => ({
            ...prev,
            isRunning: false,
            error: update.errorMessage || 'Pipeline failed',
            steps: {
              ...prev.steps,
              [step]: {
                name: stepName,
                status: 'error',
                message: update.errorMessage || 'Failed',
                data: {}
              }
            }
          }));
        }
      },
      // onError callback
      (err) => {
        console.error('[AppGrpcWeb] Stream error:', err);
        setPipelineState(prev => ({
          ...prev,
          isRunning: false,
          error: err.message || 'Stream error occurred'
        }));
      },
      // onComplete callback
      () => {
        console.log('[AppGrpcWeb] Stream ended');
        setPipelineState(prev => ({
          ...prev,
          isRunning: false
        }));
      }
    );

    setCurrentStream(stream);
  };

  const handleReset = () => {
    // Cancel current stream if any
    if (currentStream) {
      currentStream.cancel();
      setCurrentStream(null);
    }

    // Reset PipelineFlow component (clear start times)
    if (pipelineFlowRef.current) {
      pipelineFlowRef.current.reset();
    }

    // Reset pipeline state
    setPipelineState({
      isRunning: false,
      progress: 0,
      currentStep: 0,
      currentStepName: '',
      steps: {},
      error: null,
      completed: false,
      batchId: '',
      imagesDownloaded: 0,
      weedInstancesExtracted: 0
    });
  };

  const handleLogin = async () => {
    // After OAuth callback, re-check auth status
    const isAuth = await authService.isAuthenticated();
    if (isAuth) {
      const userProfile = await authService.getUserProfile();
      if (userProfile) {
        setIsAuthenticated(true);
        setUser(userProfile);
      }
    }
  };

  const handleLogout = async () => {
    try {
      console.log('[AppGrpcWeb] Logging out...');
      await authService.logout();
      setIsAuthenticated(false);
      setUser(null);
      handleReset();
      setAnchorEl(null);
    } catch (error) {
      console.error('[AppGrpcWeb] Logout error:', error);
      // Clear local state anyway
      setIsAuthenticated(false);
      setUser(null);
      setAnchorEl(null);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Show loading spinner while checking auth
  if (authLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #1ABC9C 0%, #16A085 100%)',
          }}
        >
          <CircularProgress size={60} sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ color: 'white', mt: 3 }}>
            Loading...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <Routes>
      {/* OAuth Callback Route */}
      <Route path="/callback" element={<OAuthCallback />} />

      {/* Main App Route - Protected */}
      <Route
        path="/*"
        element={
          isAuthenticated ? (
            <MainApp
              user={user}
              pipelineState={pipelineState}
              anchorEl={anchorEl}
              systemHealthy={systemHealthy}
              healthCheckLoading={healthCheckLoading}
              handlePipelineStart={handlePipelineStart}
              handleReset={handleReset}
              handleMenuOpen={handleMenuOpen}
              handleMenuClose={handleMenuClose}
              handleLogout={handleLogout}
              pipelineFlowRef={pipelineFlowRef}
            />
          ) : (
            <Login onLogin={handleLogin} />
          )
        }
      />
    </Routes>
  );
}

// Main wrapper component
function AppGrpcWeb() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

// Main App Component (extracted for cleaner routing)
const MainApp: React.FC<any> = ({
  user,
  pipelineState,
  anchorEl,
  systemHealthy,
  healthCheckLoading,
  handlePipelineStart,
  handleReset,
  handleMenuOpen,
  handleMenuClose,
  handleLogout,
  pipelineFlowRef,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine current tab based on route
  const getCurrentTab = () => {
    if (location.pathname === '/models') return 1;
    if (location.pathname === '/monitor') return 2;
    if (location.pathname === '/health') return 3;
    return 0;
  };

  return (
    <>

      {/* App Bar */}
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <Box
            component="img"
            src="/precision_ai_logo.png"
            alt="PrecisionAI"
            sx={{
              height: '32px',
              mr: 2,
              filter: 'brightness(0) invert(1)'
            }}
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            PrecisionAI Platform
          </Typography>

          {/* Navigation Tabs */}
          <Tabs
            value={getCurrentTab()}
            onChange={(_, newValue) => {
              if (newValue === 0) navigate('/');
              if (newValue === 1) navigate('/models');
              if (newValue === 2) navigate('/monitor');
              if (newValue === 3) navigate('/health');
            }}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{ mr: 2 }}
          >
            <Tab
              icon={<DashboardIcon />}
              label="Pipeline"
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<SettingsIcon />}
              label="Models"
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<HistoryIcon />}
              label="Monitor"
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<HealthIcon />}
              label="Health"
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
          </Tabs>

          {/* System Health Status */}
          <Box sx={{ mr: 2 }}>
            {healthCheckLoading ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : systemHealthy === null ? (
              <Chip
                label="Status Unknown"
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            ) : systemHealthy ? (
              <Chip
                label="● All Systems Healthy"
                size="small"
                sx={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  fontWeight: 600,
                  '& .MuiChip-label': {
                    px: 2,
                  },
                }}
              />
            ) : (
              <Chip
                label="● System Unhealthy"
                size="small"
                sx={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  fontWeight: 600,
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      opacity: 1,
                    },
                    '50%': {
                      opacity: 0.7,
                    },
                  },
                  '& .MuiChip-label': {
                    px: 2,
                  },
                }}
                onClick={() => navigate('/health')}
                clickable
              />
            )}
          </Box>

          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              {user?.username}
            </Typography>
            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              size="large"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem disabled>
                <AccountCircle sx={{ mr: 1 }} />
                {user?.username}
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ExitToApp sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{
        background: 'linear-gradient(180deg, rgba(26, 188, 156, 0.03) 0%, rgba(255, 255, 255, 0) 100%)',
        minHeight: 'calc(100vh - 64px)',
        py: 4
      }}>
        <Container maxWidth="xl">
          <Routes>
            {/* Pipeline Route */}
            <Route
              path="/"
              element={
                <Box>
                  {/* Pipeline Form - Show when idle */}
                  {!pipelineState.isRunning && !pipelineState.completed && !pipelineState.error && (
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  background: 'white',
                  borderRadius: 4,
                  boxShadow: '0px 4px 20px rgba(26, 188, 156, 0.08)',
                  border: '1px solid rgba(26, 188, 156, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0px 8px 32px rgba(26, 188, 156, 0.12)',
                  }
                }}
              >
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #1ABC9C 0%, #16A085 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px'
                      }}
                    >
                      🚀
                    </Box>
                    Start New Data Curation Pipeline
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ ml: 8, fontSize: '1.05rem', lineHeight: 1.7 }}
                  >
                    Configure and launch a new data curation pipeline to process agricultural images,
                    run semantic segmentation, and extract weed instances.
                  </Typography>
                </Box>
                <PipelineForm onPipelineStart={handlePipelineStart} user={user} />
              </Paper>
            )}

          {/* Pipeline Flow - Show when running or completed */}
          {(pipelineState.isRunning || pipelineState.completed || pipelineState.error) && (
            <>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mt: 3,
                  background: 'white',
                  borderRadius: 4,
                  boxShadow: '0px 4px 20px rgba(26, 188, 156, 0.08)',
                  border: '1px solid rgba(26, 188, 156, 0.1)',
                }}
              >
                {/* Header */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Pipeline Execution
                  </Typography>
                  <Chip
                    label={
                      pipelineState.error ? 'Failed' :
                      pipelineState.completed ? 'Completed' :
                      pipelineState.isRunning ? 'Running' :
                      'Idle'
                    }
                    color={
                      pipelineState.error ? 'error' :
                      pipelineState.completed ? 'success' :
                      pipelineState.isRunning ? 'primary' :
                      'default'
                    }
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                {/* Error Alert */}
                {pipelineState.error && (
                  <Box sx={{ mb: 3 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        backgroundColor: '#ffebee',
                        borderLeft: '4px solid #f44336',
                        borderRadius: 2
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#d32f2f' }}>
                        Pipeline Failed
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#c62828' }}>
                        {pipelineState.error}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* Success Summary */}
                {pipelineState.completed && !pipelineState.error && (
                  <Box sx={{ mb: 3 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        backgroundColor: '#e8f5e9',
                        borderLeft: '4px solid #4caf50',
                        borderRadius: 2
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2e7d32' }}>
                        Pipeline Completed Successfully!
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {pipelineState.batchId && (
                          <Typography variant="body2" sx={{ color: '#1b5e20' }}>
                            <strong>Batch ID:</strong> {pipelineState.batchId}
                          </Typography>
                        )}
                        {pipelineState.imagesDownloaded !== undefined && (
                          <Typography variant="body2" sx={{ color: '#1b5e20' }}>
                            <strong>Images Downloaded:</strong> {pipelineState.imagesDownloaded}
                          </Typography>
                        )}
                        {pipelineState.weedInstancesExtracted !== undefined && (
                          <Typography variant="body2" sx={{ color: '#1b5e20' }}>
                            <strong>Weed Instances Extracted:</strong> {pipelineState.weedInstancesExtracted}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Box>
                )}

                {/* React Flow Pipeline Visualization */}
                <PipelineFlowWithRef ref={pipelineFlowRef} />
              </Paper>

              {/* Reset Button */}
              {(pipelineState.completed || pipelineState.error) && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    sx={{ minWidth: 200 }}
                  >
                    Reset Pipeline
                  </Button>
                </Box>
              )}
            </>
          )}
                </Box>
              }
            />

            {/* Model Configuration Route */}
            <Route path="/models" element={<ModelConfiguration />} />

            {/* Pipeline Monitor Route */}
            <Route path="/monitor" element={<PipelineMonitor />} />

            {/* Health Status Route */}
            <Route path="/health" element={<HealthStatus />} />
          </Routes>
        </Container>
      </Box>
    </>
  );
};

export default AppGrpcWeb;
